import { NavLink } from "react-router-dom";
import Logo from "../assets/logo-principal.webp";
import { User } from "../services/user/userModel";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { cx } from "../react-helpers/css";
import { userService } from "../services/user/userService";
import { loggerBuilder } from "../services/logger";
import { toastsWithIntl } from "../services/toastService";
import { useMemo, useState } from "react";
import { getGuideLink } from "../services/user/userModel";
import ShellIcon from "./shell/ShellIcon";
import { useLogout } from "./shell/useLogout";

// En-tête de l'espace candidat et des pages publiques (maquette v3 : chead sur bureau, appbar et
// tiroir sur mobile). L'espace professionnel a sa propre barre (shell/ProTopbar) ; un pro connecté
// ne passe ici que sur une page publique (2'MAX employeur, consentement de sponsoring).
const CommonHeader = ({ loggedUser }: { loggedUser?: User }) => {
  const { getPremiumUserLink } = userService();
  const { t } = useTranslation();
  const { toastError } = toastsWithIntl(["common"]);
  const logger = loggerBuilder("CommonHeader");
  const logout = useLogout();
  const [loadingPremium, setLoadingPremium] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);
  const recruiterGuideLink = useMemo(
    () => getGuideLink(loggedUser?.usage),
    [loggedUser?.usage],
  );

  const isCandidate = !!loggedUser && !loggedUser.organizationId;
  const showPremium =
    isCandidate &&
    (!loggedUser.subscriptionEndDate ||
      (loggedUser.subscriptionEndDate &&
        new Date(loggedUser.subscriptionEndDate) < new Date()));

  const premiumButton = showPremium && (
    <button
      type="button"
      className="btn-prem"
      disabled={loadingPremium}
      onClick={() => {
        const handlePremiumClick = async () => {
          try {
            setLoadingPremium(true);
            const result = await getPremiumUserLink(loggedUser.id);
            if (result?.url) window.location.href = result.url;
          } catch (err) {
            logger.error("Erreur lors de la récupération du lien premium", err);
            toastError("common:error.header.PREMIUM_LINK_ERROR");
            return;
          } finally {
            setLoadingPremium(false);
          }
        };

        void handlePremiumClick();
      }}
    >
      <span className="btn-prem_z">⚡</span>
      {loadingPremium ? "..." : t("common:header.PREMIUM")}
    </button>
  );

  const brand = (
    <NavLink to={"/"} className="brand">
      <img className="brand_logo" src={Logo} alt="2 Minutes Max" />
      {isCandidate && <small>{t("common:shell.CANDIDATE_AREA")}</small>}
    </NavLink>
  );

  const close = () => setMenuOpened(false);

  return (
    <>
      <header className={cx(["chead", isCandidate && "--candidate"])}>
        {brand}
        <span className="chead_sp" />
        <LanguageSwitcher />
        {isCandidate && (
          <>
            <a
              className="tlink"
              href={t("candidate:CANDIDATE_GUIDE_LINK")}
              target="_blank"
              rel="noreferrer"
            >
              <ShellIcon name="book" />
              {t("common:header.TIPS")}
            </a>
            <NavLink className="tlink" to={"/account"}>
              <ShellIcon name="user" />
              {t("common:header.MY_ACCOUNT")}
            </NavLink>
          </>
        )}
        {!!loggedUser?.organizationId && (
          <a
            className="tlink"
            href={recruiterGuideLink}
            target="_blank"
            rel="noreferrer"
          >
            <ShellIcon name="book" />
            {t("common:header.RECRUITER_GUIDE")}
          </a>
        )}
        {premiumButton}
        {!!loggedUser && (
          <button
            type="button"
            className="iconbtn"
            onClick={logout}
            title={t("common:sidebar.buttons.LOGOUT")}
            aria-label={t("common:sidebar.buttons.LOGOUT")}
          >
            <ShellIcon name="logout" />
          </button>
        )}
        {!loggedUser && (
          <>
            <NavLink className="tlink" to="/login">
              {t("common:header.LOGIN")}
            </NavLink>
            <NavLink className="tlink" to="/register">
              {t("common:header.REGISTER")}
            </NavLink>
          </>
        )}
      </header>

      {isCandidate && (
        <header className="appbar --light">
          <button
            type="button"
            className="iconbtn"
            onClick={() => setMenuOpened(true)}
            aria-label={t("common:shell.OPEN_MENU")}
            aria-expanded={menuOpened}
          >
            <ShellIcon name="menu" />
          </button>
          {brand}
          <span className="chead_sp" />
          {premiumButton}
        </header>
      )}

      {isCandidate && menuOpened && (
        <>
          <div className="shell_scrim" onClick={close} aria-hidden="true" />
          <nav
            className="side --light --drawer"
            aria-label={t("common:shell.MAIN_NAV")}
          >
            <button
              type="button"
              className="iconbtn side_close"
              onClick={close}
              aria-label={t("common:shell.CLOSE_MENU")}
            >
              <ShellIcon name="close" />
            </button>
            <div className="side_welcome">{brand}</div>
            <div className="side_scroll">
              <div className="side_grp">
                <NavLink className="nav-a" to="/candidate" onClick={close}>
                  <ShellIcon name="home" />
                  <span>{t("common:sidebar.HOME")}</span>
                </NavLink>
                <NavLink className="nav-a" to="/account" onClick={close}>
                  <ShellIcon name="user" />
                  <span>{t("common:header.MY_ACCOUNT")}</span>
                </NavLink>
              </div>
              <div className="side_grp --sep">
                <a
                  className="nav-a"
                  href={t("candidate:CANDIDATE_GUIDE_LINK")}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ShellIcon name="book" />
                  <span>{t("common:header.TIPS")}</span>
                  <ShellIcon name="external" className="nav-a_ext" />
                </a>
                <div className="nav-a --lang">
                  <LanguageSwitcher />
                </div>
                <button type="button" className="nav-a" onClick={logout}>
                  <ShellIcon name="logout" />
                  <span>{t("common:sidebar.buttons.LOGOUT")}</span>
                </button>
              </div>
            </div>
          </nav>
        </>
      )}
    </>
  );
};

export default CommonHeader;
